import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/i18n";
import { langName } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import EmailField from "@/components/EmailField";

export const ROLES = ["superadmin", "admin", "reception", "personeel", "controller"];
export const GRANTABLE = {
  superadmin: ["superadmin", "admin", "reception", "personeel", "controller"],
  admin: ["admin", "reception", "personeel", "controller"],
};
export const FIN_CLASSES = [0, 1, 2, 3, 4, 5];
export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const emptySlots = () => [{ start: "", end: "" }, { start: "", end: "" }, { start: "", end: "" }];
export const blankUser = () => ({
  number: "", staff_number: "", last_name: "", first_name: "", nickname: "", email: "", birth_date: "", role: "reception", finance_class: 0, language: "nl", idle_minutes: 60, min_week_hours: 0, mailbox_access: false,
  phone: "", street: "", house_number: "", postal_code: "", city: "",
  workdays: Object.fromEntries(DAYS.map((d) => [d, emptySlots()])),
});

function F({ label, children, className = "" }) {
  return <div className={`space-y-1 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;
}

export default function UserForm({ form, setForm }) {
  const { t, lang } = useLang();
  const { user: me } = useAuth();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSlot = (d, i, k, v) => {
    const slots = (form.workdays[d] || emptySlots()).map((s, idx) => (idx === i ? { ...s, [k]: v } : s));
    setForm({ ...form, workdays: { ...form.workdays, [d]: slots } });
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <F label={t("u_number")}>
          <Input value={form.number} data-testid="user-number" readOnly tabIndex={-1} className="font-mono tracking-wide cursor-default" />
          <p className="text-[11px] text-muted-foreground mt-1">{t("u_number_hint")}</p>
        </F>
        <F label={t("u_staff_number")}>
          <Input value={form.staff_number || ""} onChange={set("staff_number")} data-testid="user-staff-number" />
          <p className="text-[11px] text-muted-foreground mt-1">{t("u_staff_hint")}</p>
        </F>
        <F label={t("u_nickname")}><Input value={form.nickname} onChange={set("nickname")} data-testid="user-nickname" /></F>
        <F label={t("first_name")}><Input required value={form.first_name} onChange={set("first_name")} data-testid="user-first-name" /></F>
        <F label={t("last_name")}><Input required value={form.last_name} onChange={set("last_name")} data-testid="user-last-name" /></F>
        <F label={t("email")} className="col-span-2"><EmailField required value={form.email} onChange={set("email")} testid="user-email" /></F>
        <F label={t("u_birth")}><Input type="date" max={new Date().toISOString().slice(0, 10)} value={form.birth_date} onChange={set("birth_date")} data-testid="user-birth" className={form.birth_date && form.birth_date > new Date().toISOString().slice(0, 10) ? "border-rose-400" : ""} /></F>
        <F label={t("phone")}>
          <Input value={form.phone} onChange={set("phone")} placeholder="+34 612 345 678" data-testid="user-phone" />
          <p className="text-[11px] text-muted-foreground mt-1">{t("user_phone_hint")}</p>
        </F>
        <F label={t("u_role")}>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
            <SelectContent>{(GRANTABLE[me?.role] || []).map((r) => <SelectItem key={r} value={r}>{t(`role_${r}`)}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label={t("u_finance_class")}>
          <Select value={String(form.finance_class ?? 0)} onValueChange={(v) => setForm({ ...form, finance_class: Number(v) })}>
            <SelectTrigger data-testid="user-finance-class"><SelectValue /></SelectTrigger>
            <SelectContent>{FIN_CLASSES.map((c) => <SelectItem key={c} value={String(c)}>{t(`fin_${c}`)}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">{t("u_finance_hint")}</p>
        </F>
        <F label={t("language")}>
          <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
            <SelectTrigger data-testid="user-language"><SelectValue /></SelectTrigger>
            <SelectContent>{["nl", "es", "en"].map((c) => <SelectItem key={c} value={c}>{langName(c, lang)}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label={t("u_idle")}><Input type="number" min="60" max="480" value={form.idle_minutes ?? 60} onChange={set("idle_minutes")} data-testid="user-idle" /></F>
        <F label={t("u_min_week_hours")}>
          <Input type="number" min="0" max="80" step="0.5" value={form.min_week_hours ?? 0} onChange={set("min_week_hours")} data-testid="user-min-hours" />
          <p className="text-[11px] text-muted-foreground mt-1">{t("u_min_week_hint")}</p>
        </F>
        <F label={t("u_street")} className="col-span-2"><Input value={form.street} onChange={set("street")} data-testid="user-street" /></F>
        <F label={t("u_house")}><Input value={form.house_number} onChange={set("house_number")} data-testid="user-house" /></F>
        <F label={t("u_postal")}><Input value={form.postal_code} onChange={set("postal_code")} data-testid="user-postal" /></F>
        <F label={t("u_city")} className="col-span-2"><Input value={form.city} onChange={set("city")} data-testid="user-city" /></F>
        <F label={t("u_mailbox_access")} className="col-span-2">
          <label className="flex items-center gap-2 text-sm h-10">
            <input type="checkbox" checked={!!form.mailbox_access} onChange={(e) => setForm({ ...form, mailbox_access: e.target.checked })} data-testid="user-mailbox-access" />
            {t("u_mailbox_access")}
          </label>
          <p className="text-[11px] text-muted-foreground mt-1">{t("u_mailbox_hint")}</p>
        </F>
      </div>

      <div>
        <Label className="text-xs">{t("u_workdays")}</Label>
        <div className="mt-1 rounded-lg border border-border overflow-hidden">
          <div className="grid grid-cols-[44px_repeat(3,minmax(0,1fr))] bg-muted text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1">
            <span /><span>{t("from")} – {t("to")} 1</span><span>{t("from")} – {t("to")} 2</span><span>{t("from")} – {t("to")} 3</span>
          </div>
          {DAYS.map((d) => (
            <div key={d} className="grid grid-cols-[44px_repeat(3,minmax(0,1fr))] items-center gap-2 px-2 py-1 border-t border-border" data-testid={`workday-${d}`}>
              <span className="text-xs font-semibold text-foreground/90">{t(`day_${d}`)}</span>
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-1">
                  <Input type="time" className="h-7 text-xs px-1 min-w-0 w-full" value={form.workdays[d]?.[i]?.start || ""} onChange={(e) => setSlot(d, i, "start", e.target.value)} />
                  <span className="text-xs text-muted-foreground">–</span>
                  <Input type="time" className="h-7 text-xs px-1 min-w-0 w-full" value={form.workdays[d]?.[i]?.end || ""} onChange={(e) => setSlot(d, i, "end", e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
